/**
 * Owner: Masa Bayu
 * File: AuthorDiscoveryPage.tsx
 * Date:Time: 2026-10-19 13:00
 * Description: §411 — skrin Author Discovery (/authors) replika mockup Owner:
 *   hero carian "Discover Great Authors", pil All/Fiction/Non-Fiction/Popular,
 *   Featured (kad desktop / barisan avatar mobile), karusel buku popular,
 *   rail Find an Author + Trending bernombor + Kategori Popular. Satu DOM +
 *   media query 0/768/1024; data katalog nyata, pengikut = demo deterministik.
 */

import { useMemo, useState, useSyncExternalStore } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { getCatalogVersion, subscribeCatalog } from '../../publication/catalog-port';
import {
  listMarketplaceBooks,
  MARKETPLACE_CATEGORY_ORDER,
} from '../../home/marketplace/marketplace-projection';
import { getMarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { HERO_BANNER_SLIDES } from '../../ux/hero-banners';
import {
  subscribeAuthorFollows,
  getAuthorFollowSnapshot,
  toggleAuthorFollow,
} from '../public/public-author-follow-store';
import { listPublicAuthors } from '../public/public-author-data';
import { AUTHOR_SCREEN_LABELS } from './author-discovery-labels';
import {
  featuredAuthors,
  filterAuthors,
  latestBooksByAuthor,
  popularCategoryTiles,
  trendingAuthors,
  type AuthorPill,
} from './author-discovery-data';
import {
  AuthorPillRow,
  BooksByAuthorRail,
  FeaturedAuthorCards,
  PopularAuthorRow,
  PopularCategoryGrid,
  TrendingAuthorList,
} from './AuthorDiscoverySections';
import './author-discovery.css';
import './author-discovery-books.css';

export interface AuthorDiscoveryPageProps {
  readonly onNavigate: (path: string) => void;
  readonly search?: string;
}

function initialFromSearch(search: string | undefined): { pill: AuthorPill; query: string } {
  const params = new URLSearchParams(search?.startsWith('?') ? search.slice(1) : (search ?? ''));
  const pill = params.get('pill');
  return {
    pill:
      pill === 'fiction' || pill === 'nonfiction' || pill === 'popular' || pill === 'all'
        ? pill
        : 'all',
    query: params.get('q') ?? '',
  };
}

export function AuthorDiscoveryPage({
  onNavigate,
  search,
}: AuthorDiscoveryPageProps): ReactElement {
  const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);
  const books = useMemo(() => listMarketplaceBooks(), [catalogVersion]);
  const authors = useMemo(() => listPublicAuthors(books), [catalogVersion]);
  const follows = useSyncExternalStore(subscribeAuthorFollows, getAuthorFollowSnapshot);
  const following = useMemo(() => new Set(follows), [follows]);

  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const t = useMemo(() => AUTHOR_SCREEN_LABELS[profile.language], [profile]);

  const initial = useMemo(() => initialFromSearch(search), [search]);
  const [pill, setPill] = useState<AuthorPill>(initial.pill);
  const [query, setQuery] = useState(initial.query);
  const [featuredExpanded, setFeaturedExpanded] = useState(false);
  const [trendingExpanded, setTrendingExpanded] = useState(false);

  const visible = useMemo(() => filterAuthors(authors, pill, query), [authors, pill, query]);
  // senarai penuh dihantar; komponen memotong kepada 5 dan View All mengembangkan
  const featured = useMemo(() => featuredAuthors(visible, visible.length), [visible]);
  const trending = useMemo(() => trendingAuthors(visible, visible.length), [visible]);
  const tiles = useMemo(() => popularCategoryTiles(books, MARKETPLACE_CATEGORY_ORDER), [books]);
  const railBooks = useMemo(() => latestBooksByAuthor(books), [books]);

  const submitSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (query.trim().length > 0) {
      onNavigate(`/all-books?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const sectionProps = { t, language: profile.language, onNavigate };

  return (
    <div className="qxdsc">
      <div className="qxdsc__layout">
        <div className="qxdsc__main">
          <section className="qxdsc__hero" aria-label={t.discovery.heroAria}>
            <div className="qxdsc__hero-copy">
              <h1 className="qxdsc__hero-title">
                <span className="qxdsc__hero-title-a">{t.discovery.heroTitleA}</span>{' '}
                <span className="qxdsc__hero-title-b">{t.discovery.heroTitleB}</span>
              </h1>
              <p className="qxdsc__hero-sub">{t.discovery.heroCopy}</p>
              <form className="qxdsc__hero-search" onSubmit={submitSearch}>
                <LandingIcon name="search" size={16} />
                <input
                  type="search"
                  value={query}
                  placeholder={t.discovery.searchPlaceholder}
                  aria-label={t.discovery.searchPlaceholder}
                  onChange={(event) => {
                    setQuery(event.target.value);
                  }}
                />
              </form>
            </div>
            <img
              className="qxdsc__hero-art"
              src={HERO_BANNER_SLIDES[2]?.src}
              alt=""
              aria-hidden="true"
            />
          </section>

          <AuthorPillRow
            t={t}
            pill={pill}
            onPill={setPill}
            className="qxdsc__pills qxdsc__pills--main"
          />

          <FeaturedAuthorCards
            {...sectionProps}
            authors={featured}
            expanded={featuredExpanded}
            following={following}
            onToggleFollow={toggleAuthorFollow}
            onSeeAll={() => {
              setFeaturedExpanded((prev) => !prev);
            }}
          />

          <PopularAuthorRow {...sectionProps} authors={featured} />

          <BooksByAuthorRail {...sectionProps} books={railBooks} tiles={tiles} />
        </div>

        <aside className="qxdsc__rail">
          <section className="qxdsc__card qxdsc__find" aria-label={t.discovery.findHeading}>
            <div className="qxdsc__card-head">
              <span className="qxdsc__card-icon" aria-hidden="true">
                <LandingIcon name="users" size={16} />
              </span>
              <h2 className="qxdsc__h2">{t.discovery.findHeading}</h2>
            </div>
            <form className="qxdsc__find-form" onSubmit={submitSearch}>
              <input
                type="search"
                value={query}
                placeholder={t.discovery.findPlaceholder}
                aria-label={t.discovery.findPlaceholder}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <button
                type="submit"
                className="qxdsc__find-btn"
                aria-label={t.discovery.findHeading}
              >
                <LandingIcon name="search" size={16} />
              </button>
            </form>
            <AuthorPillRow
              t={t}
              pill={pill}
              onPill={setPill}
              className="qxdsc__pills qxdsc__pills--rail"
            />
          </section>

          <TrendingAuthorList
            {...sectionProps}
            authors={trending}
            expanded={trendingExpanded}
            following={following}
            onToggleFollow={toggleAuthorFollow}
            onSeeAll={() => {
              setTrendingExpanded((prev) => !prev);
            }}
          />

          <PopularCategoryGrid {...sectionProps} tiles={tiles} />
        </aside>
      </div>
    </div>
  );
}
